import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import ReCAPTCHA from 'react-google-recaptcha';

import NavBar from '../nav/NavBar';
import { sendPasswordResetEmail } from '../../shared/requests/UserRequests';
import successIcon from '../../static/img/icons/checked_icon.svg';
import errorIcon from '../../static/img/icons/warning_icon.svg';
import { RECAPTCHA_SITEKEY } from '../../shared/Constants';

function Message(props) {
  const { status, classes } = props;
  if (status === 'pre') {
    return (
      <div className={classes.instructions}>
        <Typography className={classes.instructions} variant="body1">Enter the email address or username associated with your account to receive a password reset link via your email address on file.</Typography>
      </div>
    );
  }
  if (status === 'wait') {
    return (
      <div className={classes.instructions}>
        <Typography className={classes.instructions} variant="body1">Sending email..</Typography>
      </div>
    );
  }
  if (status === 'success') {
    return (
      <div className={classes.instructions}>
        <img src={successIcon} className={classes.icon} alt="success icon" />
        <Typography className={classes.instructions} variant="body1">An email has been sent. Check your inbox and follow the instructions.</Typography>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className={classes.instructions}>
        <img src={errorIcon} className={classes.icon} alt="error icon" />
        <Typography className={classes.instructions} variant="body1">We could not find an account associated with that email or username.</Typography>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className={classes.instructions}>
        <img src={errorIcon} className={classes.icon} alt="error icon" />
        <Typography className={classes.instructions} variant="body1">We were unable to send the confirmation email at this time.</Typography>
      </div>
    );
  }
  return (
    <div className={classes.instructions}>
      <Typography className={classes.instructions} variant="body1">Something went wrong...</Typography>
    </div>
  );
}

const styles = theme => ({
  appBarSpacer: theme.mixins.toolbar,
  header: {
    textAlign: 'center',
    margin: theme.spacing(5),
  },
  loadingSpinner: {
    margin: theme.spacing(5),
  },
  wrapper: {
    padding: theme.spacing(3),
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  container: {
    width: 'auto',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(400 + theme.spacing(2 * 2))]: {
      width: 400,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  instructions: {
    textAlign: 'center',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(3),
  },
  button: {
    marginBottom: theme.spacing(),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  icon: {
    height: 40,
    width: 40,
    marginBottom: theme.spacing(3),
  },
  helpText: {
    fontSize: '.8em',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    textAlign: 'center',
  },
  recaptcha: {
    marginBottom: theme.spacing(2),
    width: 'inherit',
    display: 'flex',
    justifyContent: 'center',
  },
});

class RecoverAccountPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      status: 'pre',
      email: '',
      errors: {},
      errorStates: {
        email: false,
      },
      recaptcha: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onRecaptchaChange = this.onRecaptchaChange.bind(this);
    this.onRecaptchaError = this.onRecaptchaError.bind(this);
  }

  handleChange(e) {
    this.setState({ email: e.target.value });
  }

  handleSubmit(e) {
    e.preventDefault();
    const { email } = this.state;
    this.setState({ status: 'wait' });
    sendPasswordResetEmail(email)
      .then((response) => {
        // console.log('email sent');
        // console.log(response.data);
        this.setState({ status: 'success' });
      })
      .catch((error) => {
        console.log('failed to send email');
        console.log(error.response);
        if (error.response.status === 404) {
          this.setState({ status: 'failed' });
        } else {
          this.setState({ status: 'error' });
        }
      });
  }

  onRecaptchaChange(value) {
    // console.log('Captcha value: ', value);
    if (value == null) {
      this.setState({ recaptcha: false });
    } else {
      this.setState({ recaptcha: true });
    }
  }

  onRecaptchaError(e) {
    this.setState({ recaptcha: false });
  }


  render() {
    const { classes } = this.props;
    const {
      status,
      email,
      errors,
      errorStates,
      recaptcha,
    } = this.state;

    function buttonState() {
      if (!errorStates.email && recaptcha) {
        return false;
      }
      return true;
    }

    return (
      <React.Fragment>
        <NavBar />
        <div className={classes.container}>
          <div className={classes.appBarSpacer} />
          <Typography className={classes.header} color="textSecondary" variant="h4">Account Recovery</Typography>
          <Paper className={classes.paper} elevation={1}>
            <div className={classes.wrapper}>
              <Message status={status} classes={classes} />
              {status !== 'success'
                ? (
                  <form className={classes.form} noValidate autoComplete="off" onSubmit={this.handleSubmit}>
                    <TextField
                      color="secondary"
                      fullWidth
                      id="email"
                      label="Email or Username"
                      className={classes.textField}
                      value={email}
                      onChange={this.handleChange}
                      variant="outlined"
                      error={errorStates.email}
                      helperText={errors.email}
                    />
                    {/* RECAPTCHA */}
                    <ReCAPTCHA
                      sitekey={RECAPTCHA_SITEKEY}
                      onChange={this.onRecaptchaChange}
                      onErrored={this.onRecaptchaError}
                      className={classes.recaptcha}
                    />
                    <Button disabled={buttonState()} fullWidth className={classes.button} variant="contained" color="secondary" type="submit">Submit</Button>
                  </form>
                )
                : null
              }
            </div>
            <Typography className={classes.helpText} variant="subtitle1">Forget the username or email address associated with your nianow.com account? Contact customer service at nia@nianow.com.</Typography>
            {status === 'wait' ? <LinearProgress color="secondary" /> : null}
          </Paper>
        </div>
      </React.Fragment>
    );
  }
}

RecoverAccountPage.propTypes = {
  classes: PropTypes.object.isRequired,
};

Message.propTypes = {
  status: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(RecoverAccountPage);
